import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency } from "@/lib/constants";
import { BarChart3, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function ComparablesCard({ opp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/opportunities/${opp.id}/comparables?radius_miles=25`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [opp.id]);

  return (
    <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5" data-testid="comparables-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <BarChart3 size={16} className="text-[#d4a574]" /> Comparable Sales
        </h3>
        {data && (
          <div className="text-[10px] font-mono text-[#7a6d5a] uppercase tracking-wider">
            {data.summary?.count} comps · median ${data.summary?.median_ppsf}/sqft
          </div>
        )}
      </div>
      {loading && <div className="flex items-center justify-center py-8 text-[#7a6d5a]"><Loader2 size={16} className="animate-spin" /></div>}
      {!loading && data && (
        <>
          {data.summary?.count > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="border border-[#33291f] rounded p-2 bg-[#14100c]"><div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Median $/sqft</div><div className="font-mono text-[15px] text-[#d4a574] font-semibold">${data.summary.median_ppsf}</div></div>
              <div className="border border-[#33291f] rounded p-2 bg-[#14100c]"><div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Min</div><div className="font-mono text-[13px]">${data.summary.min_ppsf}</div></div>
              <div className="border border-[#33291f] rounded p-2 bg-[#14100c]"><div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Max</div><div className="font-mono text-[13px]">${data.summary.max_ppsf}</div></div>
              <div className="border border-[#33291f] rounded p-2 bg-[#14100c]"><div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Avg Price</div><div className="font-mono text-[13px]">{currency(data.summary.avg_price)}</div></div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="comps-table">
              <thead className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">
                <tr className="border-b border-[#33291f]">
                  <th className="text-left py-1.5">Comp</th>
                  <th className="text-right py-1.5">Sqft</th>
                  <th className="text-right py-1.5">Price</th>
                  <th className="text-right py-1.5">$/sqft</th>
                  <th className="text-right py-1.5">Miles</th>
                  <th className="text-right py-1.5">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.comps.map((c) => (
                  <tr key={c.id} className="border-b border-[#33291f]/50 hover:bg-[#241d16]">
                    <td className="py-1.5">
                      {c.linked_opportunity_id ? (
                        <Link to={`/app/opportunities/detail/${c.linked_opportunity_id}`} className="hover:text-[#d4a574]">{c.title.slice(0, 40)}</Link>
                      ) : (
                        <span>{c.title.slice(0, 40)}</span>
                      )}
                      <span className="text-[9px] text-[#7a6d5a] ml-1 uppercase tracking-wider">{c.kind}</span>
                    </td>
                    <td className="text-right font-mono">{c.sqft.toLocaleString()}</td>
                    <td className="text-right font-mono text-[#d4a574]">{currency(c.sale_price)}</td>
                    <td className="text-right font-mono">${c.price_per_sqft.toFixed(0)}</td>
                    <td className="text-right font-mono text-[#b8ac96]">{c.distance_miles}</td>
                    <td className="text-right font-mono text-[10px] text-[#7a6d5a]">{c.sale_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[9px] text-[#7a6d5a] mt-3 font-mono uppercase tracking-wider">
            Provider: {data.provider} · <span className="text-[#d4a574]">Plug in ATTOM/HouseCanary/CoreLogic for licensed comps</span>
          </div>
        </>
      )}
    </div>
  );
}
